// Whether a row's reports folder actually holds anything to view.
//
// `reports` on a row is a POINTER, not proof of files. patient_table.py's
// restore_reports_after_reset stamps every patients_with_ehrid row with its
// computed folder on every rebuild, and a visit keeps its pointer after the
// folder has been emptied. Rendering "View Reports" off the pointer alone put
// the button on every patient record and opened an empty folder.
//
// The schedules grid solves the same problem with its own rules, including
// sibling-folder heuristics (scheduleFoldersWithFiles below); foldersWithFiles
// is the plain version for /search.

/** Left behind when a folder is created in the Supabase dashboard. Not a report. */
export const EMPTY_FOLDER_PLACEHOLDER = '.emptyFolderPlaceholder';

/** Supabase's list() page size when no limit is passed. */
const LIST_PAGE_SIZE = 100;

// Runs `job` over `items`, at most `concurrency` at a time.
async function eachBounded(items, concurrency, job) {
  const queue = [...items];
  const worker = async () => {
    while (queue.length) await job(queue.shift());
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker));
}

/**
 * True when a storage listing holds anything worth opening: a file, or a
 * subfolder (a patient folder's dated visit folders). Storage folders only
 * exist while something is inside them, so a subfolder entry counts.
 */
export function hasFolderContent(entries) {
  return (entries || []).some((e) => e?.name && e.name !== EMPTY_FOLDER_PLACEHOLDER);
}

/**
 * The subset of `folders` that hold content. Each distinct folder is listed
 * once, at most `concurrency` at a time — a blank search returns hundreds of
 * rows, and hundreds of parallel list calls is how a page gets rate-limited.
 * A folder that cannot be listed counts as empty; it must not fail the batch.
 */
export async function foldersWithFiles(supabase, bucket, folders, { concurrency = 8 } = {}) {
  const found = new Set();
  await eachBounded(new Set((folders || []).filter(Boolean)), concurrency, async (folder) => {
    try {
      const { data, error } = await supabase.storage.from(bucket).list(folder);
      if (!error && hasFolderContent(data)) found.add(folder);
    } catch (e) {
      console.warn(`[search] could not list ${folder}:`, e?.message);
    }
  });
  return found;
}

/**
 * What a results-table reports cell should render for a row:
 *   'none'    — nothing to open (no pointer, or the folder was found empty)
 *   'pending' — the folders are still being listed
 *   'button'  — open the folder
 * `foldersWithFiles === undefined` means the caller never checks folders; such
 * tables keep rendering the button off the pointer alone.
 */
export function reportCellState(folder, foldersWithFiles, checked) {
  if (!folder) return 'none';
  if (foldersWithFiles === undefined) return 'button';
  if (!checked) return 'pending';
  return foldersWithFiles.has(folder) ? 'button' : 'none';
}

// --- Schedules grid ---------------------------------------------------------
// SchedulesTable used to decide each row with its own chain of listings
// (checkFilesExist): up to four per row, every time `schedules` changed, and
// each one doubled by the RDS storage overlay. The rules below are that
// function's, unchanged; only the listings are shared between rows.

// A "file" to the schedules grid is any entry with a dot in its name, so the
// dashboard placeholder counts here, unlike in hasFolderContent.
const hasDottedEntry = (entries) => (entries || []).some((item) => item.name.includes('.'));

// {base}/{patient folder}/{date folder}, split exactly as the grid always has.
function parseReportsFolder(folder) {
  const pathParts = folder.split('/');
  let basePath = '';
  let patientFolder = '';
  let dateFolder = '';
  if (pathParts.length >= 3) {
    basePath = pathParts.slice(0, -2).join('/');
    patientFolder = pathParts[pathParts.length - 2];
    dateFolder = pathParts[pathParts.length - 1];
  }
  // Patient name: everything before the first underscore.
  const patientNameMatch = patientFolder ? patientFolder.match(/^(.+?)_/) : null;
  const patientName = patientNameMatch ? patientNameMatch[1] : patientFolder;
  return { folder, basePath, patientFolder, dateFolder, patientName };
}

// The folders of a schema-folder listing that belong to `patientName`: same
// name before the underscore, ignoring case and spacing around commas.
function matchingPatientFolders(entries, patientName) {
  const normalizedPatientName = patientName
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*,\s*/g, ', ');
  return entries.filter((folder) => {
    if (folder.name.includes('.')) return false;
    const folderNameLower = folder.name.toLowerCase();
    const folderBaseName = folderNameLower.includes('_')
      ? folderNameLower.split('_')[0].trim()
      : '';
    const normalizedFolderBaseName = folderBaseName
      .replace(/\s+/g, ' ')
      .replace(/\s*,\s*/g, ', ');
    return (
      normalizedFolderBaseName === normalizedPatientName ||
      folderNameLower.startsWith(`${normalizedPatientName}_`) ||
      folderNameLower.startsWith(`${normalizedPatientName} `)
    );
  });
}

// Whether the row's own folder can hold anything, judged from its schema
// folder's listing. Storage's search matches a listed prefix with ILIKE (case
// ignored, `_` and `%` as wildcards), so every entry the folder name could
// match that way counts, and only a complete listing can rule it out: a
// skipped listing must never change an answer.
function ownFolderMayExist(row, schemaEntries) {
  if (!schemaEntries || schemaEntries.length >= LIST_PAGE_SIZE) return true;
  const pattern = row.patientFolder
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/_/g, '.')
    .replace(/%/g, '.*');
  const like = new RegExp(`^${pattern}$`, 'i');
  return schemaEntries.some((entry) => like.test(entry.name));
}

// Lists each path once, at most `concurrency` at a time, through `listings`
// (path -> pending result) so overlapping checks share a listing. A failed
// listing is not kept, so the next check asks again. list() reports failures
// in `error`; a throw is treated the same way.
async function listEach(supabase, bucket, paths, listings, concurrency) {
  const wanted = [...new Set(paths)];
  const fresh = [];
  for (const path of wanted) {
    if (listings.has(path)) continue;
    let settle;
    listings.set(path, new Promise((resolve) => { settle = resolve; }));
    fresh.push({ path, settle });
  }
  const pending = wanted.map((path) => [path, listings.get(path)]);
  await eachBounded(fresh, concurrency, async ({ path, settle }) => {
    let result;
    try {
      result = await supabase.storage.from(bucket).list(path);
    } catch (error) {
      result = { data: null, error };
    }
    if (!result || result.error || !result.data) listings.delete(path);
    settle(result);
  });
  const results = new Map();
  for (const [path, promise] of pending) results.set(path, await promise);
  return results;
}

/**
 * The subset of `folders` (schedule rows' reports folders) holding a report,
 * by the rules the schedules grid has always applied to each row:
 *   1. a file in the row's own folder ({base}/{Name_EHR}/{date}); else
 *   2. a patient folder in {base} matching the row's patient name whose
 *      folder for the row's date holds a file.
 * Each {base} (the schema folder) is listed once for all the rows; its
 * listing usually shows that a row's own folder does not exist, so that
 * folder is listed only when it can. Pass `listings` (a Map) to share
 * listings between calls; a failed one is never kept.
 */
export async function scheduleFoldersWithFiles(
  supabase,
  bucket,
  folders,
  { concurrency = 8, listings = new Map() } = {},
) {
  const rows = [...new Set((folders || []).filter(Boolean))].map(parseReportsFolder);
  const list = (paths) => listEach(supabase, bucket, paths, listings, concurrency);
  const entriesOf = (result) => (result && !result.error && result.data ? result.data : null);
  const found = new Set();

  const bases = await list(rows.filter((r) => r.basePath && r.patientName).map((r) => r.basePath));
  const schemaEntries = (r) =>
    (r.basePath && r.patientName ? entriesOf(bases.get(r.basePath)) : null);

  // 1. The row's own folder.
  const own = await list(
    rows.filter((r) => ownFolderMayExist(r, schemaEntries(r))).map((r) => r.folder),
  );
  const ownEmpty = new Set();
  const fallback = [];
  for (const r of rows) {
    const listed = own.get(r.folder);
    const entries = listed === undefined ? [] : entriesOf(listed);
    if (hasDottedEntry(entries)) {
      found.add(r.folder);
      continue;
    }
    if (entries) ownEmpty.add(r.folder);
    if (schemaEntries(r)) fallback.push(r);
  }

  // 2. Same-name patient folders, then each one's folder for the row's date.
  const candidates = fallback.flatMap((r) =>
    matchingPatientFolders(schemaEntries(r), r.patientName)
      .map((folder) => ({ r, patientPath: `${r.basePath}/${folder.name}` }))
      // The row's own folder again: already known to hold no file.
      .filter(({ patientPath }) => !(ownEmpty.has(r.folder) && `${patientPath}/${r.dateFolder}` === r.folder)),
  );
  const patientListings = await list(candidates.map((c) => c.patientPath));
  const dated = candidates.filter(({ r, patientPath }) =>
    (entriesOf(patientListings.get(patientPath)) || []).some(
      (item) => !item.name.includes('.') && item.name === r.dateFolder,
    ),
  );
  const dateListings = await list(dated.map(({ r, patientPath }) => `${patientPath}/${r.dateFolder}`));
  for (const { r, patientPath } of dated) {
    if (hasDottedEntry(entriesOf(dateListings.get(`${patientPath}/${r.dateFolder}`)))) {
      found.add(r.folder);
    }
  }
  return found;
}
